import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from "@mui/material";
import { AppAction, AppContext } from "helpers/context";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { GroupLabel } from "helpers/models";
import { formatAmount } from "helpers/helpers";
import { countData } from "helpers/groupResultData";

interface Props {
  tableData: any;
  activeLegend: any;
  noAnalysisLocationRef?: any;
  analysedLegends: any;
  type: string;
  isLineAnalysis?: boolean;
  distanceUnit?: "m" | "km";
  distanceMax?: number;
}

const ShareTable = ({
  tableData = [],
  activeLegend,
  noAnalysisLocationRef = { current: [] },
  analysedLegends,
  type,
  isLineAnalysis = false,
  distanceUnit,
  distanceMax
}: Readonly<Props>) => {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [active, setActive] = useState("sortIndex");
  const [direction, setDirection] = useState<{ [key: string]: "asc" | "desc" }>(
    { sortIndex: "asc", count: "asc", percentage: "asc" }
  );
  const [sortTableData, setSortTableData] = useState<any>([]);

  const unit =
    context.currentMultipleLocationsAnalysed?.aggregationUnit ??
    "numberOfLocations";
  const isNumberOfLocations = unit === "numberOfLocations";
  let {
    rowCount,
    totalCount,
    rowPointIds,
    totalPointIds,
    unitCount,
    unitTotalCount
  } = countData(tableData, unit);

  const handleGetIndeterminateAll = () => {
    return (
      (rowCount > 0 || noAnalysisLocationRef.current.length > 0) &&
      totalCount !== rowCount
    );
  };

  const handleGetIndeterminate = (row: any) => {
    let num = row.pointIds.filter((item: any) => item.checked).length;
    return num > 0 && num < row.pointIds.length;
  };

  const handleGetNoAnalysisLocationPercent = () => {
    if (isNumberOfLocations) {
      let count = totalCount + noAnalysisLocationRef.current.length;
      if (count === 0) {
        return "-";
      }
      const percentage =
        ((noAnalysisLocationRef.current.length / count) * 100).toFixed(2) + "%";
      return percentage;
    } else {
      let num = 0;
      let count = unitCount;
      noAnalysisLocationRef.current.forEach((item: any) => {
        num += Math.round(item[unit]);
        count += Math.round(item[unit]);
      });

      if (count === 0) {
        return "-";
      }
      const percentage = ((num / count) * 100).toFixed(2) + "%";
      return percentage;
    }
  };

  const handleGetLocationsCount = (row: any) => {
    if (isLineAnalysis) {
      const percentage = row.pointIds.length / totalCount;
      return (distanceMax ? distanceMax * percentage : 0).toFixed(3);
    } else {
      const count = row.pointIds.filter((item: any) => item.checked).length;
      return count || "-";
    }
  };

  const handleGetLocationsUnit = (row: any) => {
    let num = 0;
    row.pointIds.forEach((item: any) => {
      if (item.checked) {
        num += Math.round(item[unit]);
      }
    });
    return formatAmount(num) || "-";
  };

  const handleGetLocationsPercentage = (row: any) => {
    if (isLineAnalysis) {
      const percentage = (row.pointIds.length / totalCount * 100).toFixed(2) + "%";
      return percentage;
    } else {
      if (isNumberOfLocations) {
        const count = row.pointIds.filter((item: any) => item.checked).length;
        let all = totalCount + noAnalysisLocationRef.current.length;
        if (all === 0) {
          return "-";
        }
        const percentage = ((count / all) * 100).toFixed(2) + "%";
        return percentage;
      } else {
        let num = 0;
        let count = unitCount;
        row.pointIds.forEach((item: any) => {
          if (item.checked) {
            num += Math.round(item[unit]);
          }
        });

        noAnalysisLocationRef.current.forEach((item: any) => {
          count += Math.round(item[unit]);
        });
        if (count === 0) {
          return "-";
        }
        const percentage = ((num / count) * 100).toFixed(2) + "%";
        return percentage;
      }
    }

  };

  const handleTableSubLabel = () => {
    if (type === "event") {
      return t(
        "legend." + (analysedLegends?.[activeLegend]?.subtitle || "Peak Gust")
      );
    } else {
      if (tableData?.[0]) {
        if (tableData[0].layerId === "CS_Tornado_Global_SwissRe") {
          return t("legend.Tornado Days (Tornado ≥ EF1) per Year");
        } else if (tableData[0].layerId === "CS_Lightning_Global_SwissRe") {
          return t("legend.Lightning Days (≥ 1 detected event) per Year");
        } else if (tableData[0].subtitle) {
          return t("legend." + tableData[0].subtitle);
        } else if (tableData[0].title) {
          return t("legend." + tableData[0].title);
        }
      } else {
        return t("legend." + analysedLegends?.[activeLegend].subtitle);
      }
    }
  };

  const handleGetUnitTotalText = () => {
    let num = unitCount;
    noAnalysisLocationRef.current.forEach((item: any) => {
      num += Math.round(item[unit]);
    });
    return formatAmount(num) || "-";
  };

  const handleCheckedStatus = (row: any) => {
    return row?.every((item: any) => item.checked);
  };

  const handleCheckedAll = (evt: any) => {
    const checked = evt.target.checked;

    context.currentMultipleLocations!.items =
      context.currentMultipleLocations!.items?.map((location: any) => {
        if (totalPointIds.includes(location.id.split("-").join(""))) {
          location.checked = checked;
        }
        return location;
      });

    dispatch({
      type: AppAction.setCurrentMultipleLocations,
      payload: context.currentMultipleLocations
    });
  };

  const handleCheckedRow = (evt: any, row: GroupLabel) => {
    const checked = evt.target.checked;
    let pointIds = row.pointIds.map((data: any) => data.id.split("-").join(""));

    context.currentMultipleLocations!.items =
      context.currentMultipleLocations!.items?.map((location: any) => {
        if (pointIds.includes(location.id.split("-").join(""))) {
          location.checked = checked;
        }
        return location;
      });

    dispatch({
      type: AppAction.setCurrentMultipleLocations,
      payload: context.currentMultipleLocations
    });
  };

  const handleDeleteNoData = () => {
    context.currentMultipleLocations!.items =
      context.currentMultipleLocations!.items?.map((location: any) => {
        noAnalysisLocationRef.current.forEach((item: any) => {
          let itemId = item.id.split("-").join("");
          let locationId = location.id.split("-").join("");
          if (itemId === locationId) {
            location.checked = false;
          }
        });
        return location;
      });
    dispatch({
      type: AppAction.setCurrentMultipleLocations,
      payload: context.currentMultipleLocations
    });
  };

  const handleGetHeaderCount = () => {
    if (isLineAnalysis) {
      return distanceUnit === "m" ? t("drawShape.length(m)") : t("drawShape.length(km)");
    } else {
      return t("locations.table.locations");
    }
  };

  const handleSort = (property: string, data = tableData) => {
    if (!data.length) {
      return;
    }

    setActive(property);
    direction[property] = direction[property] === "asc" ? "desc" : "asc";
    setDirection({ ...direction });
    handleSetSortData(data, direction, property);
  };

  const handleSetSortData = (
    data = tableData,
    directionObj = direction,
    property = active
  ) => {
    if (!data.length) {
      return;
    }

    let key = property;
    if (key === "percentage") {
      if (isNumberOfLocations) {
        key = "count";
      } else {
        key = unit;
      }
    }
    const sortedTableData = [...data];
    sortedTableData.sort((a: any, b: any) => {
      if (!a || !b) {
        return 0;
      }

      let aValue = undefined;
      let bValue = undefined;
      if (key === "count") {
        aValue = a.pointIds.filter(
          (item: { checked: any }) => item.checked
        ).length;
        bValue = b.pointIds.filter(
          (item: { checked: any }) => item.checked
        ).length;
      } else if (key === unit) {
        aValue = a.pointIds.reduce((accumulator: number, currentItem: any) => {
          return accumulator + Math.round(currentItem[key] || 0);
        }, 0);
        bValue = b.pointIds.reduce((accumulator: number, currentItem: any) => {
          return accumulator + Math.round(currentItem[key] || 0);
        }, 0);
      } else {
        aValue = a?.[key];
        bValue = b?.[key];
      }

      if (aValue === undefined) {
        return directionObj[property] === "asc" ? -1 : 1;
      }
      if (bValue === undefined) {
        return directionObj[property] === "asc" ? 1 : -1;
      }
      if (aValue < bValue) {
        return directionObj[property] === "asc" ? 1 : -1;
      }
      if (aValue > bValue) {
        return directionObj[property] === "asc" ? -1 : 1;
      }
      return 0;
    });

    setSortTableData(sortedTableData);
  };

  useEffect(() => {
    handleSetSortData();
  }, [tableData]);

  return (
    <TableContainer sx={{ width: "auto", height: "100%" }}>
      <Table
        stickyHeader
        aria-label="sticky table"
        size="small"
        style={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: "100%"
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontSize: "12px",
                color: "#000000de"
              }
            }}
          >
            {!isLineAnalysis && (
              <TableCell style={{ padding: 0, width: 44 }} align="center">
                <Checkbox
                  color="success"
                  indeterminate={handleGetIndeterminateAll()}
                  checked={rowCount === totalCount}
                  onClick={(evt: any) => handleCheckedAll(evt)}
                  inputProps={{
                    "aria-label": "select all desserts"
                  }}
                />
              </TableCell>
            )}
            <TableCell align="left" style={{ width: "130px", padding: "8px" }}>
              <TableSortLabel
                active={active === "sortIndex"}
                onClick={() => handleSort("sortIndex")}
                direction={direction.sortIndex}
              >
                {handleTableSubLabel()}
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" style={{ width: "90px", padding: "8px" }}>
              <TableSortLabel
                active={active === "count"}
                onClick={() => handleSort("count")}
                direction={direction.count}
              >
                {handleGetHeaderCount()}
              </TableSortLabel>
            </TableCell>
            {!isLineAnalysis && !isNumberOfLocations && (
              <>
                <TableCell
                  align="right"
                  style={{ width: "120px", padding: "8px" }}
                >
                  <TableSortLabel
                    active={active === "currency"}
                    onClick={() => handleSort("currency")}
                    direction={direction["currency"]}
                  >
                    {t("locations.add.currency")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  style={{ width: "120px", padding: "8px" }}
                >
                  <TableSortLabel
                    active={active === unit}
                    onClick={() => handleSort(unit)}
                    direction={direction[unit]}
                  >
                    {t("locations." + unit)}
                  </TableSortLabel>
                </TableCell>
              </>
            )}
            <TableCell
              align="right"
              style={{ width: "85px", padding: "8px 16px 8px 8px" }}
            >
              <TableSortLabel
                active={active === "percentage"}
                onClick={() => handleSort("percentage")}
                direction={direction.percentage}
              >
                {t("locations.table.share")}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortTableData.map((row: any) => (
            <TableRow
              key={row.label}
              sx={{
                "& td": {
                  fontSize: "13px",
                  color: "#000000de"
                }
              }}
            >
              {!isLineAnalysis && (
                <TableCell style={{ padding: 0 }} align="center">
                  <Checkbox
                    color="success"
                    checked={handleCheckedStatus(row.pointIds)}
                    indeterminate={handleGetIndeterminate(row)}
                    onClick={(evt) => handleCheckedRow(evt, row)}
                  />
                </TableCell>
              )}
              <TableCell align="left" style={{ padding: "8px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      flexShrink: 0,
                      width: "16px",
                      height: "16px",
                      marginRight: "5px",
                      display: "inline-block",
                      border: "1px solid #e3e3e3",
                      backgroundColor: row.color,
                      opacity: row.opacity
                    }}
                  ></span>
                  {row.valueLabel || row.label
                    ? t("legend." + (row.valueLabel || row.label))
                    : ""}
                </div>
              </TableCell>

              <TableCell align="right" style={{ padding: "8px" }}>
                {handleGetLocationsCount(row)}
              </TableCell>
              {!isLineAnalysis && !isNumberOfLocations && (
                <>
                  <TableCell align="right" style={{ padding: "8px" }}>
                    {context.currentMultipleLocations?.currency}
                  </TableCell>
                  <TableCell align="right" style={{ padding: "8px" }}>
                    {handleGetLocationsUnit(row)}
                  </TableCell>
                </>
              )}

              <TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>
                {handleGetLocationsPercentage(row)}
              </TableCell>
            </TableRow>
          ))}
          {!isLineAnalysis && noAnalysisLocationRef.current.length > 0 && (
            <TableRow
              sx={{
                "& td": {
                  fontSize: "13px",
                  color: "#000000de"
                }
              }}
            >
              <TableCell style={{ padding: 0 }} align="center">
                <Checkbox
                  color="success"
                  checked={true}
                  onClick={handleDeleteNoData}
                />
              </TableCell>
              <TableCell align="left" style={{ padding: "8px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      flexShrink: 0,
                      width: "16px",
                      height: "16px",
                      boxSizing: "border-box",
                      marginRight: "5px",
                      display: "inline-block",
                      backgroundColor: "rgb(242, 242, 242)",
                      border: "2.5px dotted #959595"
                    }}
                  ></span>
                  {t("legend.Not Analyzed")}
                </div>
              </TableCell>
              <TableCell align="right" style={{ padding: "8px" }}>
                {noAnalysisLocationRef.current.length}
              </TableCell>
              {!isLineAnalysis && !isNumberOfLocations && (
                <>
                  <TableCell align="right" style={{ padding: "8px" }}>
                    {context.currentMultipleLocations?.currency}
                  </TableCell>
                  <TableCell align="right" style={{ padding: "8px" }}>
                    {formatAmount(
                      noAnalysisLocationRef.current.reduce(
                        (count: number, i: any) =>
                          (count += Math.round(i[unit])),
                        0
                      )
                    ) || "-"}
                  </TableCell>
                </>
              )}
              <TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>
                {handleGetNoAnalysisLocationPercent()}
              </TableCell>
            </TableRow>
          )}
          <TableRow
            style={{
              fontWeight: "bold",
              height: isLineAnalysis ? "auto" : "50px"
            }}
            sx={{
              "& td": {
                fontSize: "13px",
                color: "#000000de"
              }
            }}
          >
            <TableCell
              style={{ padding: "8px" }}
              colSpan={isLineAnalysis ? 1 : 2}
            >
              {t("multiple.total")}
            </TableCell>
            <TableCell align="right" style={{ padding: "8px" }}>
              {isLineAnalysis
                ? parseFloat(distanceMax?.toFixed(3) || "0")
                : rowCount + noAnalysisLocationRef.current.length}
            </TableCell>
            {!isLineAnalysis && !isNumberOfLocations && (
              <>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {context.currentMultipleLocations?.currency}
                </TableCell>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {handleGetUnitTotalText()}
                </TableCell>
              </>
            )}
            <TableCell align="right" style={{ padding: "8px 16px 8px 8px" }}>
              100%
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ShareTable;


